import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Routes, Route, Outlet } from "react-router-dom";
import DefaultEntry from "../components/DefaultEntry.jsx";
import MilkEntries from "../components/MilkEntries.jsx";
import UserList from "../components/reusable/usersList.jsx";
import FatPrices from "../components/reusable/FatInputs.jsx";
import CowPriceEntryGrid from "../components/reusable/CowPriceEntryGrid.jsx";
import { AnimatePresence, motion } from "framer-motion";
import PageHeader from "../components/reusable/PageHeader.jsx";

function Entry({ role }) {
  const [userId, setUserId] = useState("");
  const [Name, setUserName] = useState("");
  // 1. State for the popup
  const [showNoCustomerAlert, setShowNoCustomerAlert] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 850);
  
  const navigate = useNavigate();
  const location = useLocation();

  const PAGE_TITLES = [
    { match: "/milk-entry", title: "Milk Entry" },
    { match: "/ghee-entry", title: "Ghee Entry" },
    { match: "/feed-entry", title: "Feed Entry" },
    { match: "/receive-money", title: "Receive Money" },
    { match: "/give-money", title: "Give Money" },
  ];

  const pageTitle =
    PAGE_TITLES.find((p) => location.pathname.includes(p.match))?.title ||
    "Milk Entries";

  useEffect(() => {
    const resize = () => setIsSmallScreen(window.innerWidth < 850);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // 2. Callback function to check user count from UserList
  const handleUserDataLoaded = (users) => {
    if (Array.isArray(users) && users.length === 0 && role==="admin") {
      setShowNoCustomerAlert(true);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 relative">
      <PageHeader title={pageTitle} />

      {/* --- 3. POP UP MODAL START --- */}
      {showNoCustomerAlert && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center border border-gray-100"
          >
            <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Customers</h3>
            <p className="text-gray-600 mb-6 font-medium">
              As there is no customer, please add a customer.
            </p>
            <div className="flex gap-3">
               <button 
                onClick={() => navigate(-1)}
                className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => {
                    setShowNoCustomerAlert(false);
                    navigate("/addCustomer"); 
                }}
                className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors"
              >
                Add Customer
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {/* --- POP UP MODAL END --- */}

      {role === "admin" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 ">
          <label className="text-sm font-medium mb-1 block">
            Select Customer
          </label>
          <UserList 
            onSelectUser={setUserId} 
            onUserChange={setUserName} 
            // 4. Pass the handler here
            onDataLoaded={handleUserDataLoaded} 
          />
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.15 }}
          >
            <Routes location={location}>
              <Route path="*" element={<MilkEntries userId={userId} role={role} Name={Name} />} />
              <Route path="/feed-entry" element={<DefaultEntry userId={userId} userSelectedOption="Feed" role={role} Name={Name} />} />
              <Route path="/ghee-entry" element={<DefaultEntry userId={userId} userSelectedOption="Ghee" role={role} Name={Name} />} />
              <Route path="/receive-money" element={<DefaultEntry userId={userId} userSelectedOption="Money Received" role={role} Name={Name} />} />
              <Route path="/give-money" element={<DefaultEntry userId={userId} userSelectedOption="Money Given" role={role} Name={Name} />} />
              <Route path="/buffalo/prices" element={<FatPrices tableTitle="Buffalo" />} />
              <Route path="/cow/prices" element={<FatPrices tableTitle="Cow" />} />
              <Route path="/buffalo/prices/snf" element={<CowPriceEntryGrid tableTitle="Buffalo-snf" />} />
              <Route path="/cow/prices/snf" element={<CowPriceEntryGrid tableTitle="Cow-snf" />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>

      <Outlet />
    </div>
  );
}

export default Entry;